import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout/Layout';
import {
  CategoryEntry,
  getCategories,
  saveCategories,
} from '../../data/categoriesStore';
import styles from './Categories.module.css';

const makeId = (name: string) =>
  name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const Categories: React.FC = () => {
  const [categories, setCategories] = useState<CategoryEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Ajout catégorie
  const [newCatName, setNewCatName] = useState('');

  // Renommer catégorie
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Sous-catégories
  const [newSubName, setNewSubName] = useState('');
  const [renamingSubIdx, setRenamingSubIdx] = useState<number | null>(null);
  const [renameSubValue, setRenameSubValue] = useState('');

  useEffect(() => {
    setCategories(getCategories());
  }, []);

  const persist = (updated: CategoryEntry[]) => {
    setCategories(updated);
    saveCategories(updated);
  };

  const selectedCat = categories.find((c) => c.id === selectedId) ?? null;

  /* ── Catégories ── */
  const handleAddCategory = () => {
    const name = newCatName.trim();
    if (!name) return;
    if (categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      alert('Cette catégorie existe déjà.');
      return;
    }
    const entry: CategoryEntry = { id: makeId(name), name, subCategories: [] };
    persist([...categories, entry]);
    setNewCatName('');
    setSelectedId(entry.id);
  };

  const handleDeleteCategory = (id: string) => {
    if (!window.confirm('Supprimer cette catégorie et toutes ses sous-catégories ?')) return;
    const updated = categories.filter((c) => c.id !== id);
    persist(updated);
    if (selectedId === id) setSelectedId(updated[0]?.id ?? null);
  };

  const handleStartRename = (cat: CategoryEntry) => {
    setRenamingId(cat.id);
    setRenameValue(cat.name);
  };

  const handleConfirmRename = () => {
    const name = renameValue.trim();
    if (!name || !renamingId) return;
    persist(categories.map((c) => c.id === renamingId ? { ...c, name } : c));
    setRenamingId(null);
  };

  /* ── Sous-catégories ── */
  const handleAddSub = () => {
    const name = newSubName.trim();
    if (!name || !selectedCat) return;
    if (selectedCat.subCategories.some((s) => s.toLowerCase() === name.toLowerCase())) {
      alert('Cette sous-catégorie existe déjà.');
      return;
    }
    persist(categories.map((c) =>
      c.id === selectedId ? { ...c, subCategories: [...c.subCategories, name] } : c
    ));
    setNewSubName('');
  };

  const handleDeleteSub = (idx: number) => {
    if (!selectedCat) return;
    persist(categories.map((c) =>
      c.id === selectedId
        ? { ...c, subCategories: c.subCategories.filter((_, i) => i !== idx) }
        : c
    ));
  };

  const handleStartRenameSub = (idx: number) => {
    setRenamingSubIdx(idx);
    setRenameSubValue(selectedCat!.subCategories[idx]);
  };

  const handleConfirmRenameSub = () => {
    const name = renameSubValue.trim();
    if (!name || renamingSubIdx === null || !selectedCat) return;
    persist(categories.map((c) =>
      c.id === selectedId
        ? {
            ...c,
            subCategories: c.subCategories.map((s, i) => (i === renamingSubIdx ? name : s)),
          }
        : c
    ));
    setRenamingSubIdx(null);
  };

  const handleMoveSub = (idx: number, dir: -1 | 1) => {
    if (!selectedCat) return;
    const subs = [...selectedCat.subCategories];
    const target = idx + dir;
    if (target < 0 || target >= subs.length) return;
    [subs[idx], subs[target]] = [subs[target], subs[idx]];
    persist(categories.map((c) => c.id === selectedId ? { ...c, subCategories: subs } : c));
  };

  return (
    <Layout>
      <div className={styles.container}>
        <h1 className={styles.title}>Catégories</h1>
        <p className={styles.subtitle}>
          Gérez vos catégories et sous-catégories. Les modifications s'appliquent immédiatement dans les formulaires produits.
        </p>

        <div className={styles.columns}>

          {/* ── COLONNE GAUCHE : liste catégories ── */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h2 className={styles.panelTitle}>Catégories ({categories.length})</h2>
            </div>

            <ul className={styles.list}>
              {categories.map((cat) => (
                <li
                  key={cat.id}
                  className={`${styles.listItem} ${selectedId === cat.id ? styles.listItemActive : ''}`}
                  onClick={() => { setSelectedId(cat.id); setRenamingId(null); setRenamingSubIdx(null); }}
                >
                  {renamingId === cat.id ? (
                    <div className={styles.renameRow} onClick={(e) => e.stopPropagation()}>
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmRename(); if (e.key === 'Escape') setRenamingId(null); }}
                        className={styles.renameInput}
                      />
                      <button onClick={handleConfirmRename} className={styles.btnConfirm}>✓</button>
                      <button onClick={() => setRenamingId(null)} className={styles.btnCancelSm}>✕</button>
                    </div>
                  ) : (
                    <>
                      <span className={styles.listItemName}>{cat.name}</span>
                      <span className={styles.listItemSub}>{cat.subCategories.length} sous-cat.</span>
                      <div className={styles.listItemActions}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleStartRename(cat); }}
                          className={styles.btnIcon}
                          title="Renommer"
                        >✏️</button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }}
                          className={styles.btnIconDanger}
                          title="Supprimer"
                        >🗑️</button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>

            {/* Ajouter catégorie */}
            <div className={styles.addRow}>
              <input
                type="text"
                placeholder="Nouvelle catégorie..."
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); }}
                className={styles.addInput}
              />
              <button onClick={handleAddCategory} className={styles.btnAdd}>+ Ajouter</button>
            </div>
          </div>

          {/* ── COLONNE DROITE : sous-catégories ── */}
          <div className={styles.panel}>
            {!selectedCat ? (
              <div className={styles.emptyState}>
                Sélectionnez une catégorie pour gérer ses sous-catégories.
              </div>
            ) : (
              <>
                <div className={styles.panelHeader}>
                  <h2 className={styles.panelTitle}>
                    Sous-catégories de <span className={styles.catName}>{selectedCat.name}</span>
                    <span className={styles.countBadge}>{selectedCat.subCategories.length}</span>
                  </h2>
                </div>

                {selectedCat.subCategories.length === 0 ? (
                  <div className={styles.emptyStateSm}>Aucune sous-catégorie.</div>
                ) : (
                  <ul className={styles.subList}>
                    {selectedCat.subCategories.map((sub, idx) => (
                      <li key={idx} className={styles.subItem}>
                        {renamingSubIdx === idx ? (
                          <div className={styles.renameRow}>
                            <input
                              autoFocus
                              value={renameSubValue}
                              onChange={(e) => setRenameSubValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmRenameSub(); if (e.key === 'Escape') setRenamingSubIdx(null); }}
                              className={styles.renameInput}
                            />
                            <button onClick={handleConfirmRenameSub} className={styles.btnConfirm}>✓</button>
                            <button onClick={() => setRenamingSubIdx(null)} className={styles.btnCancelSm}>✕</button>
                          </div>
                        ) : (
                          <>
                            <div className={styles.subOrder}>
                              <button onClick={() => handleMoveSub(idx, -1)} className={styles.btnOrder} disabled={idx === 0}>▲</button>
                              <button onClick={() => handleMoveSub(idx, 1)} className={styles.btnOrder} disabled={idx === selectedCat.subCategories.length - 1}>▼</button>
                            </div>
                            <span className={styles.subName}>{sub}</span>
                            <div className={styles.subActions}>
                              <button onClick={() => handleStartRenameSub(idx)} className={styles.btnIcon} title="Renommer">✏️</button>
                              <button onClick={() => handleDeleteSub(idx)} className={styles.btnIconDanger} title="Supprimer">🗑️</button>
                            </div>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Ajouter sous-catégorie */}
                <div className={styles.addRow}>
                  <input
                    type="text"
                    placeholder="Nouvelle sous-catégorie..."
                    value={newSubName}
                    onChange={(e) => setNewSubName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddSub(); }}
                    className={styles.addInput}
                  />
                  <button onClick={handleAddSub} className={styles.btnAdd}>+ Ajouter</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
